import { type ApiErrorDetail, apiErrorBodySchema, type ErrorCode } from '@coworker/contracts';

/**
 * A non-2xx answer from the control API, decoded - ADR-030.
 *
 * Two shapes arrive, and telling them apart is the whole job of this file:
 *
 *  * everything Hono answers is the ADR-030 envelope, `{ error: { code, message,
 *    traceId, details? } }`;
 *  * **a gateway 401 is not.** An HTTP API cannot customise the body its JWT
 *    authorizer returns, so a request with no token or an expired one comes back
 *    as `{"message":"Unauthorized"}` with no code and no trace id. ADR-032 records
 *    it as the one response outside the contract. Parsing it as the envelope
 *    fails, and a client that then reports "malformed response" sends a person
 *    looking for a bug in the API when their session simply expired.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The API's closed union, plus the codes only this client can mint.
   *
   * ADR-030: the SPA switches on `code` and never parses `message`, so a client
   * failure needs its own code rather than borrowing `INTERNAL` - "the API said
   * something broke" and "we gave up waiting" are different things to a caller
   * and must not be separable only by string-matching prose. These three are
   * deliberately *not* in `ERROR_CODES`: they never cross the wire, and adding
   * them there would change the contract to say the server can send them.
   */
  readonly code: ErrorCode | 'UNAUTHENTICATED_AT_GATEWAY' | 'UNREADABLE' | 'TIMEOUT';
  readonly traceId: string | undefined;
  readonly details: readonly ApiErrorDetail[] | undefined;

  constructor(init: {
    status: number;
    code: ApiError['code'];
    message: string;
    traceId?: string;
    details?: readonly ApiErrorDetail[];
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.traceId = init.traceId;
    this.details = init.details;
  }

  /** The session is gone or was never valid: re-authenticate rather than retry. */
  get isUnauthenticated(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'UNAUTHENTICATED_AT_GATEWAY';
  }

  /** A real token whose subject has no profile yet. AUTH-010 finishes the sign-up. */
  get needsOnboarding(): boolean {
    return this.code === 'USER_NOT_PROVISIONED';
  }
}

/** Never throws. A decoder that can fail turns one error into two. */
export async function decodeApiError(response: Response): Promise<ApiError> {
  const body: unknown = await response
    .json()
    .then((value: unknown) => value)
    .catch(() => undefined);

  const envelope = apiErrorBodySchema.safeParse(body);
  if (envelope.success) {
    const { code, message, traceId, details } = envelope.data.error;
    return new ApiError({ status: response.status, code, message, traceId, details });
  }

  if (response.status === 401) {
    return new ApiError({
      status: 401,
      code: 'UNAUTHENTICATED_AT_GATEWAY',
      message: 'the session has expired or the token was refused',
    });
  }

  return new ApiError({
    status: response.status,
    code: 'UNREADABLE',
    message: `the API answered ${response.status} with a body this client does not recognise`,
  });
}

/**
 * A message a person can read, from whatever was thrown.
 *
 * Extracted when the approvals screen needed the same line - which is the
 * moment the copy would have been made. **There were already two**, in
 * `Chat.tsx` and `Integrations.tsx`, identical and independent; two versions of
 * "how an error reads" is how one screen quietly starts saying "Something went
 * wrong" for an error the other explains.
 */
export function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong';
}
